<script setup>
  import { onBeforeUnmount, onMounted, ref, watch } from "vue";
  import { useBookingStore } from '@/stores/booking';
  import "intl-tel-input/build/css/intlTelInput.css";

  const emit = defineEmits(["change"]);

  const props = defineProps({
    modelValue: String,
    error: String,
    showError: Boolean,
    helperText: String,
    handlePhoneChange: Function
  });

  const bookingStore = useBookingStore();

  watch(
    () => bookingStore.state?.country_code,
    (newVal) => {
      if (newVal && iti) {
        iti.setCountry(newVal.toLowerCase());
        adjustPadding();
      }
    }
  );

  const phoneInput = ref(null);
  let iti;

  onBeforeUnmount(() => {
    if (phoneInput.value) {
      phoneInput.value.removeEventListener("input", updateValue);
      phoneInput.value.removeEventListener("countrychange", updateValue);
    }

    if (iti) {
      iti.destroy();
      iti = null;
    }

    delete window.iti;
  });

  onMounted(async () => {
    if (!phoneInput.value) return;

    const { default: intlTelInput } = await import("intl-tel-input");

    iti = intlTelInput(phoneInput.value, {
      initialCountry: "gb",
      autoPlaceholder: "polite",
      separateDialCode: true,
      loadUtils: () =>
        import("intl-tel-input/utils")
    });

    phoneInput?.value.addEventListener("input", updateValue);
    phoneInput?.value.addEventListener("countrychange", updateValue);
    window.iti = iti;
    adjustPadding();
  });

  const adjustPadding = () => {
    if (!phoneInput.value) return;

    const dialCodeEl = phoneInput.value
      .closest(".iti")
      ?.querySelector(".iti__selected-dial-code");

    const dialCodeLength =
      dialCodeEl?.innerText.replace(/\D/g, "").length || 0;

    phoneInput.value.style.paddingLeft =
      `${70 + dialCodeLength * 8}px`;
  };

  const updateValue = () => {
    if (!iti || !phoneInput.value) return;

    const countryData = iti.getSelectedCountryData();

    const payload = {
      fullNumber: iti.getNumber(),
      nationalNumber: iti.getNumber(undefined, 2),
      countryCode: countryData.iso2,
      dialCode: countryData.dialCode,
      isValid: iti.isValidNumber(),
      rawInput: phoneInput.value.value,
    };

    emit("change", payload);
  };

  const handleInput = (event) => {
    event.target.value = event.target.value.replace(/[^\d\s\-+()]/g, '').slice(0, 20);
    props.handlePhoneChange;
  };

</script>

<template>
  <div class="w-full">

    <!-- Input -->
    <div
      :class="[
        'relative flex items-center w-full px-4 py-3 rounded-full border transition-all',
        showError && error
          ? 'border-[#CC3D40] error-box-shadow'
          : 'border-[#A0A2A4]'
      ]"
    >
      <input
        ref="phoneInput"
        type="tel"
        class="phone-input w-full bg-transparent outline-none"
        @input=handleInput
      />
    </div>

    <!-- Error -->
    <p v-if="showError && error" class="text-[#CC3D40] text-sm mt-1 err-sub-text">
      {{ error }}
    </p>

    <!-- Helper -->
    <p v-if="helperText" class="text-sm text-[#888B8D] mt-1" v-html="helperText"></p>

  </div>
</template>

<style>
    .error-box-shadow {
      box-shadow: 0 0 0 3px rgba(235, 177, 179, 0.30);
    }

  /* input box styles starts */

  .iti {
    width: 100%;
  }

  .iti__selected-country-primary {
    background-color: transparent !important;
    padding: 0;
  }

  .iti__flag {
    transform: scaleX(1.5) scaleY(2);
    border-radius: 50%;
    margin-right: 4px;
    margin-left: 4px;
  }

  .iti__flag-container {
    display: flex;
    align-items: center;
  }

  .iti__arrow {
    margin-right: 12px;
  }

  .iti__selected-dial-code {
    margin-right: 4px;
    margin-left: 0 !important;
  }

  .iti__selected-flag {
    display: flex;
    align-items: center;
    padding: 0 !important;
    background: transparent !important;
  }

  /* input box styles ends */


  /* dropdown styles start */
  .iti__dropdown-content {
    border-radius: 24px;
  }

  .iti__search-icon {
    display: none;
  }

  .iti__search-input {
      border-bottom: 1px solid #a0a2a4 !important;
      border-top-left-radius: 24px;
      border-top-right-radius: 24px;
      color: #161412;
      padding: 14px 16px;
      font-size: 14px;
      font-family: inherit;
      outline: none;
  }

  .iti__country-list {
    padding: 8px 0
  }

  .iti__country {
      display: flex;
      align-items: center;
      padding: 10px 16px;
      cursor: pointer;
      transition: background-color 0.15s ease;
      gap: 12px;
  }

  .iti__country-name {
      display: flex;
      flex: 1;
      font-size: 14px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
  }

  .iti__dial-code {
      font-size: 14px;
      flex-shrink: 0;
      margin-left: auto;
  }
  /* dropdown styles ends */

</style>
